import React from "react";

const getPaperDisplayId = (paper) => paper?.paperId || paper?.id || "-";

const getPaperStatusLabel = (status) => {
  const normalized = (status || "PENDING").trim().toUpperCase();
  return (
    {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      UNDER_REVIEW: "Under Review",
      REVIEWED: "Reviewed",
      ACCEPTED: "Accepted",
      REJECTED: "Rejected",
      MINOR_REVISION: "Minor Revision",
      MAJOR_REVISION: "Major Revision",
      REVISION_REQUESTED: "Revision Requested",
    }[normalized] || normalized
  );
};

const getStatusColor = (status) => {
  const normalized = (status || "PENDING").trim().toUpperCase();
  if (normalized === "ACCEPTED") return "#28a745";
  if (normalized === "REJECTED") return "#dc3545";
  if (normalized === "DRAFT") return "#6c757d";
  if (
    normalized === "MINOR_REVISION" ||
    normalized === "MAJOR_REVISION" ||
    normalized === "REVISION_REQUESTED"
  ) {
    return "#fd7e14";
  }
  return "#17a2b8";
};

const PaperListSummary = ({ submittedPapers }) => {
  if (!Array.isArray(submittedPapers) || submittedPapers.length <= 1) {
    return null;
  }

  return (
    <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: "15px",
            fontSize: "18px",
            color: "#212529",
          }}
        >
          📄 Your Papers for this Conference
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa" }}>
                <th style={paperListHeaderStyle}>Paper ID</th>
                <th style={paperListHeaderStyle}>Title</th>
                <th style={paperListHeaderStyle}>Track</th>
                <th style={paperListHeaderStyle}>Mode</th>
                <th style={{ ...paperListHeaderStyle, textAlign: "center" }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {submittedPapers.map((paper) => (
                <tr
                  key={paper.id || paper.paperId || paper.title}
                  style={{ borderTop: "1px solid #e9ecef" }}
                >
                  <td style={paperListCellStyle}>{getPaperDisplayId(paper)}</td>
                  <td style={paperListCellStyle}>{paper.title || "-"}</td>
                  <td style={paperListCellStyle}>
                    {paper.trackName || paper.track || paper.trackId || "-"}
                  </td>
                  <td style={paperListCellStyle}>
                    {paper.presentationMode || "-"}
                  </td>
                  <td style={{ ...paperListCellStyle, textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor: getStatusColor(paper.status),
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {getPaperStatusLabel(paper.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const paperListHeaderStyle = {
  padding: "10px",
  textAlign: "left",
  borderBottom: "2px solid #dee2e6",
};

const paperListCellStyle = {
  padding: "10px",
  verticalAlign: "top",
};

export const PaperSubmissionReviewStep = ({
  formData,
  paperTitle,
  selectedTrackName,
  presentationMode,
  submissionMethod,
  normalizedPaperApprovalStatus,
  paperApprovalStatus,
  isRevisionPaperStatus,
  submittedPapers,
}) => (
  <>
    <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#e3f2fd",
          border: "2px solid #2196f3",
          borderRadius: "8px",
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: "15px",
            fontSize: "18px",
            color: "#1565c0",
          }}
        >
          👤 Registration Details
        </h3>
        <div style={{ lineHeight: "1.8", fontSize: "15px", color: "#333" }}>
          <div style={{ marginBottom: "12px" }}>
            <strong>Name:</strong> {formData.title} {formData.firstName}{" "}
            {formData.lastName}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Email:</strong> {formData.email}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Phone:</strong> {formData.mobile}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Country:</strong> {formData.country}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>City:</strong> {formData.city}
          </div>
        </div>
      </div>
    </div>

    <PaperListSummary submittedPapers={submittedPapers} />

    <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#e8f5e9",
          border: "2px solid #4caf50",
          borderRadius: "8px",
        }}
      >
        <h3
          style={{
            marginTop: 0,
            marginBottom: "15px",
            fontSize: "18px",
            color: "#212529",
          }}
        >
          ✅ Paper Submission Summary
        </h3>
        <div style={{ lineHeight: "1.8", fontSize: "15px", color: "#333" }}>
          <div style={{ marginBottom: "12px" }}>
            <strong>Paper Title:</strong> {paperTitle}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Track:</strong> {selectedTrackName}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Presentation Mode:</strong> {presentationMode}
          </div>
          <div style={{ marginBottom: "12px" }}>
            <strong>Submission Method:</strong> {submissionMethod}
          </div>
          <div
            style={{
              marginTop: "20px",
              padding: "12px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              fontSize: "13px",
              color: "#666",
            }}
          >
            <strong>Status:</strong>{" "}
            {normalizedPaperApprovalStatus === "SUBMITTED"
              ? "📤 Submitted for Review"
              : normalizedPaperApprovalStatus === "DRAFT"
              ? "💾 Saved as Draft"
              : isRevisionPaperStatus
              ? "📝 Revision Requested"
              : normalizedPaperApprovalStatus}
          </div>
        </div>
      </div>
    </div>

    {(paperApprovalStatus === "SUBMITTED" || isRevisionPaperStatus) && (
      <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fff3cd",
            border: "2px solid #ffc107",
            borderRadius: "8px",
            lineHeight: "1.7",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              fontSize: "16px",
              fontWeight: "600",
              color: "#856404",
            }}
          >
            {isRevisionPaperStatus ? "📝 Revision Requested" : "⏳ Paper Under Review"}
          </div>
          {isRevisionPaperStatus ? (
            <>
              <div
                style={{
                  fontSize: "15px",
                  color: "#856404",
                  marginBottom: "10px",
                }}
              >
                Your paper requires revision. Please update the paper according
                to reviewer feedback and resubmit.
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#856404",
                  fontStyle: "italic",
                }}
              >
                You can review the submission summary, go back to edit, and then
                resubmit the revised paper.
              </div>
            </>
          ) : (
            <>
              <div
                style={{
                  fontSize: "15px",
                  color: "#856404",
                  marginBottom: "10px",
                }}
              >
                Your paper has been submitted for review. The review team will
                evaluate your submission and provide feedback.
              </div>
              <div
                style={{
                  fontSize: "14px",
                  color: "#856404",
                  fontStyle: "italic",
                }}
              >
                You will receive an email notification once your paper has been
                reviewed and approved. Please log in again to continue with your
                registration.
              </div>
            </>
          )}
        </div>
      </div>
    )}

    {paperApprovalStatus === "DRAFT" && (
      <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
        <div
          style={{
            padding: "16px",
            backgroundColor: "#e8f4f8",
            border: "2px solid #00bcd4",
            borderRadius: "8px",
            lineHeight: "1.7",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              fontSize: "16px",
              fontWeight: "600",
              color: "#00838f",
            }}
          >
            📝 Draft Saved
          </div>
          <div
            style={{ fontSize: "15px", color: "#00838f", marginBottom: "10px" }}
          >
            Your paper has been saved as draft. You can submit it anytime you're
            ready.
          </div>
          <div
            style={{ fontSize: "14px", color: "#00838f", fontStyle: "italic" }}
          >
            You can continue with your registration now and submit the paper
            later, or submit it immediately before proceeding.
          </div>
        </div>
      </div>
    )}
  </>
);
